/**
 * Format datetime in Pakistan Standard Time (PKT) - UTC+5
 * Uses the same formatting approach as the clock in Layout.js for consistency
 * @param {string|Date} dateInput - Date string or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in PKT (DD/MM/YYYY, HH:MM:SS)
 */
export function formatLocalDateTime(dateInput, options = {}) {
  if (!dateInput) return 'Unknown date';
  
  // Parse the date input
  let date;
  if (typeof dateInput === 'string') {
    // Backend timestamps from database are typically UTC but without timezone info
    // If the string doesn't have timezone info (no 'Z', '+', or '-' after the time),
    // we need to treat it as UTC by appending 'Z'
    let dateString = dateInput.trim();
    
    // Check if it already has timezone info
    // Look for 'Z' at the end, or timezone offset like '+05:00' or '-05:00'
    const hasTimezone = dateString.endsWith('Z') || 
                        /[+-]\d{2}:?\d{2}$/.test(dateString);
    
    // If no timezone info and it's an ISO-like format (contains 'T'), assume UTC
    // Backend database timestamps are stored as UTC but returned without timezone indicator
    if (!hasTimezone && dateString.includes('T')) {
      // Remove any trailing milliseconds, then append 'Z' to indicate UTC
      // Format: YYYY-MM-DDTHH:MM:SS or YYYY-MM-DDTHH:MM:SS.mmm
      dateString = dateString.replace(/\.\d+$/, '') + 'Z';
    }
    
    date = new Date(dateString);
  } else {
    date = dateInput;
  }
  
  if (Number.isNaN(date.getTime())) return 'Unknown date';

  // Pakistan Standard Time (PKT) is UTC+5
  // Use formatToParts to get reliable timezone conversion (same approach as Layout.js clock)
  const defaultOptions = {
    timeZone: 'Asia/Karachi',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',  // Use 'numeric' to get raw hour value for manual formatting
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };

  const formatter = new Intl.DateTimeFormat('en-US', {
    ...defaultOptions,
    ...options,
  });

  // Use formatToParts for reliable timezone conversion (same approach as Layout.js clock)
  const parts = formatter.formatToParts(date);
  
  // Extract parts
  const year = parts.find(p => p.type === 'year')?.value || '';
  const month = parts.find(p => p.type === 'month')?.value || '';
  const day = parts.find(p => p.type === 'day')?.value || '';
  let hour = parts.find(p => p.type === 'hour')?.value || '0';
  const minute = parts.find(p => p.type === 'minute')?.value || '00';
  const second = parts.find(p => p.type === 'second')?.value || '00';
  
  // Fix hour if it's 24 (should be 00)
  if (hour === '24') {
    hour = '00';
  } else if (hour && parseInt(hour) < 10) {
    hour = hour.padStart(2, '0');
  }
  
  // Format as: DD/MM/YYYY, HH:MM:SS (matching the clock format in Layout.js)
  const dateStr = `${day}/${month}/${year}`;
  const timeStr = `${hour}:${minute}:${second}`;
  
  return `${dateStr}, ${timeStr}`;
}


